// src/pages/admin/tabs/amenities/AmenityEditPage.jsx
import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AdminAPI } from '../../../../api/admin';
import { Card, Row2, Field, Actions, Btn } from '../../ui';
import MediaPickerModal from '../../components/MediaPickerModal';
import { normalizeMediaUrl } from '../../../../api/base';

const nonEmpty = (x)=> x!=null && String(x).trim()!=='';

// Поддержим разные ключи, в зависимости от to_dict()
function extractCoverPreview(a){
  const raw = (
    a?.cover_thumb || a?.cover_thumbnail || a?.cover_url || a?.cover ||
    a?.cover_media?.thumbnail_url || a?.cover_media?.url || ''
  ) || '';
  return normalizeMediaUrl(raw);
}

export default function AmenityEditPage(){
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();

  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');

  const [name_ru, setNameRu] = React.useState('');
  const [name_en, setNameEn] = React.useState('');
  const [name_zh, setNameZh] = React.useState('');
  const [description_ru, setDescriptionRu] = React.useState('');
  const [description_en, setDescriptionEn] = React.useState('');
  const [description_zh, setDescriptionZh] = React.useState('');
  const [icon, setIcon] = React.useState('');
  const [is_active, setActive] = React.useState(true);

  const [coverPreview, setCoverPreview] = React.useState('');
  const [cover_media_id, setCoverMediaId] = React.useState(undefined);
  const [pickerOpen, setPickerOpen] = React.useState(false);

  React.useEffect(()=>{
    let stop=false;
    (async()=>{
      if(!isEdit) return;
      try{
        const d = await AdminAPI.getAmenity(id, { locale: 'ru' });
        const a = d?.amenity || d?.item || d;
        if (stop || !a) return;
        setNameRu(a.name_ru ?? a.name ?? '');
        setNameEn(a.name_en ?? a.name ?? '');
        setNameZh(a.name_zh ?? '');
        setDescriptionRu(a.description_ru ?? a.description ?? '');
        setDescriptionEn(a.description_en ?? '');
        setDescriptionZh(a.description_zh ?? '');
        setIcon(a.icon || '');
        setActive(a.is_active ?? true);
        setCoverPreview(extractCoverPreview(a));
        setCoverMediaId(a.cover_media?.id || a.cover_media_id || undefined);
        
        // Отладочная информация
        console.log('✅ Amenity loaded:', a);
      } catch(e) {
        console.error('❌ Error loading amenity:', e);
        setErr(e?.payload?.error?.message || e?.payload?.message || e?.message || 'Не удалось загрузить удобство');
      }
    })();
    return ()=>{ stop=true; };
  }, [isEdit, id]);

  const onPickFromLibrary = (list)=>{
    const file = list?.[0]; if (!file) return;
    setCoverMediaId(file.id);
    setCoverPreview(normalizeMediaUrl(file.thumbnail_url || file.url || ''));
    };

  const clearCover = ()=>{ 
    setCoverMediaId(undefined); 
    setCoverPreview(''); 
  };

  const submit = async ()=>{
    setSaving(true); setErr(''); setOk('');
    try{
      if (!nonEmpty(name_ru) && !nonEmpty(name_en)) throw new Error('Укажите название (RU или EN)');

      const payload = {
        name_ru: nonEmpty(name_ru) ? name_ru : undefined,
        name_en: nonEmpty(name_en) ? name_en : (nonEmpty(name_ru) ? name_ru : undefined),
        name_zh: nonEmpty(name_zh) ? name_zh : undefined,
        description_ru: nonEmpty(description_ru) ? description_ru : undefined,
        description_en: nonEmpty(description_en) ? description_en : undefined,
        description_zh: nonEmpty(description_zh) ? description_zh : undefined,
        icon: nonEmpty(icon) ? icon : undefined,
        is_active: !!is_active,
        cover_media_id: (cover_media_id ?? undefined),
      };

      if (isEdit) {
        await AdminAPI.updateAmenity(id, payload);
        nav('/admin/amenities');
        return;
      } else {
        await AdminAPI.createAmenity(payload);
        nav('/admin/amenities');
        return;
      }
    }catch(e){
      setErr(
        e?.payload?.details
          ? (typeof e.payload.details === 'string' ? e.payload.details : JSON.stringify(e.payload.details))
          : (e?.payload?.error?.message || e?.payload?.message || e?.message || 'Ошибка сохранения')
      );
    }finally{ setSaving(false); }
  };

  return (
    <>
      <Actions style={{marginBottom:12}}>
        <Link to="/admin/amenities" className="btn">← К списку удобств</Link>
      </Actions>

      <Card>
        <h3>{isEdit ? 'Редактировать удобство' : 'Создать удобство'}</h3>
        {err && (
          <div style={{color:'#e66', marginBottom:8}}>
            {typeof err === 'string' ? err : (err?.message || err?.error || JSON.stringify(err))}
          </div>
        )}
        {ok  && <div style={{color:'#2a8f2a', marginBottom:8}}>{ok}</div>}

        <Row2>
          <Field>Название RU<input value={name_ru} onChange={e=>setNameRu(e.target.value)} /></Field>
          <Field>Название EN<input value={name_en} onChange={e=>setNameEn(e.target.value)} /></Field>
        </Row2>
        <Row2>
          <Field>Название ZH (中文)<input value={name_zh} onChange={e=>setNameZh(e.target.value)} placeholder="游泳池, 健身房, 停车场..." /></Field>
        </Row2>
        <Row2>
          <Field>Описание RU<textarea value={description_ru} onChange={e=>setDescriptionRu(e.target.value)} rows={3} placeholder="Краткое описание удобства" /></Field>
          <Field>Описание EN<textarea value={description_en} onChange={e=>setDescriptionEn(e.target.value)} rows={3} placeholder="Brief description of amenity" /></Field>
        </Row2>
        <Row2>
          <Field>Описание ZH (中文)<textarea value={description_zh} onChange={e=>setDescriptionZh(e.target.value)} rows={3} placeholder="设施的简要说明" /></Field>
        </Row2>
        <Row2>
          <Field>Иконка (emoji/текст)<input value={icon} onChange={e=>setIcon(e.target.value)} placeholder="например: 🏊‍♂️ или fa-pool" /></Field>
        </Row2>
        <Row2>
          <Field>Активно
            <select value={is_active ? '1' : '0'} onChange={e=>setActive(e.target.value==='1')}>
              <option value="1">Да</option>
              <option value="0">Нет</option>
            </select>
          </Field>
        </Row2>

        <hr style={{border:'none', borderTop:'1px dashed var(--bdr)', margin:'12px 0'}} />

        <div style={{marginBottom: 20}}>
          <h3 style={{marginBottom: 12, color: 'var(--fg)', fontSize: 18}}>Изображение удобства</h3>
          
          {coverPreview && (
            <div style={{marginBottom:12}}>
              <img src={normalizeMediaUrl(coverPreview)} alt="" style={{width:120, height:120, objectFit:'cover', borderRadius:12, border:'1px solid var(--bdr)'}} />
              {cover_media_id && <div style={{fontSize:12, opacity:.7, marginTop:4}}>media_id: <b>{cover_media_id}</b></div>}
            </div>
          )}
          
          <div style={{display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center'}}>
            <Btn 
              onClick={()=>setPickerOpen(true)}
              $kind="primary"
            >
              Выбрать из медиатеки
            </Btn>
            {coverPreview && (
              <Btn 
                onClick={clearCover}
                $kind="danger"
              >
                Убрать
              </Btn>
            )}
          </div>
          
          <div style={{fontSize: 12, color: 'var(--muted)', marginTop: 8}}>
            Загрузите изображение для отображения удобства на сайте. Рекомендуемый размер: 400x400px
          </div>
        </div>

        <Actions style={{marginTop:14}}>
          <Btn $kind="primary" onClick={submit} disabled={saving}>
            {saving ? 'Сохранение…' : (isEdit ? 'Сохранить' : 'Создать')}
          </Btn>
          <Link to="/admin/amenities" className="btn">Отмена</Link>
        </Actions>
      </Card>

      <MediaPickerModal open={pickerOpen} onClose={()=>setPickerOpen(false)} onPick={onPickFromLibrary} multi={false} />
    </>
  );
}
